import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, startAfter, DocumentData } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Clock, Search, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface AdminLog {
  id: string;
  action: string;
  details: any;
  adminId: string;
  adminEmail: string;
  timestamp: Date;
}

const ACTION_TYPES = {
  create: { label: 'Creación', color: 'text-green-600 bg-green-50' },
  update: { label: 'Actualización', color: 'text-blue-600 bg-blue-50' },
  delete: { label: 'Eliminación', color: 'text-red-600 bg-red-50' },
  approve: { label: 'Aprobación', color: 'text-purple-600 bg-purple-50' },
  reject: { label: 'Rechazo', color: 'text-orange-600 bg-orange-50' },
  update_report: { label: 'Actualización de Reporte', color: 'text-indigo-600 bg-indigo-50' },
  delete_report: { label: 'Eliminación de Reporte', color: 'text-rose-600 bg-rose-50' },
  email_sent: { label: 'Email Enviado', color: 'text-emerald-600 bg-emerald-50' }
};

export default function AdminLogs() {
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState<DocumentData | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const LOGS_PER_PAGE = 20;

  const fetchLogs = async (isInitial = false) => {
    try {
      let q = query(
        collection(db, 'adminLogs'),
        orderBy('timestamp', 'desc'),
        limit(LOGS_PER_PAGE)
      );

      if (!isInitial && lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const newLogs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      })) as AdminLog[];

      setLogs(prevLogs => isInitial ? newLogs : [...prevLogs, ...newLogs]);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === LOGS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching admin logs:', error);
      toast.error('Error al cargar los logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(true);
  }, []);

  const getActionStyle = (action: string) => {
    return ACTION_TYPES[action as keyof typeof ACTION_TYPES]?.color || 'text-gray-600 bg-gray-50';
  };

  const getActionLabel = (action: string) => {
    return ACTION_TYPES[action as keyof typeof ACTION_TYPES]?.label || action;
  };

  const filteredLogs = logs.filter(log => {
    if (filter !== 'all' && log.action !== filter) return false;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        log.action.toLowerCase().includes(searchLower) ||
        log.adminEmail.toLowerCase().includes(searchLower) ||
        JSON.stringify(log.details).toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <AlertCircle className="h-6 w-6 mr-2 text-primary" />
          Logs de Actividad
        </h2>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">Todos</option>
            {Object.entries(ACTION_TYPES).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="relative">
          {/* Header - Fixed */}
          <div className="sticky top-0 z-10">
            <div className="bg-gray-50 border-b border-gray-200">
              <div className="min-w-[1000px]">
                <div className="grid grid-cols-[200px_200px_120px_1fr] gap-4 px-6 py-3">
                  <div className="text-xs font-medium text-gray-500 uppercase">Fecha y Hora</div>
                  <div className="text-xs font-medium text-gray-500 uppercase">Administrador</div>
                  <div className="text-xs font-medium text-gray-500 uppercase">Acción</div>
                  <div className="text-xs font-medium text-gray-500 uppercase">Detalles</div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Body - Scrollable */}
          <div className="overflow-x-auto">
            <div className="min-w-[1000px]">
              {filteredLogs.map((log) => (
                <div 
                  key={log.id}
                  className="grid grid-cols-[200px_200px_120px_1fr] gap-4 px-6 py-4 hover:bg-gray-50 items-center border-b border-gray-200"
                >
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="h-4 w-4 mr-2 text-gray-400" />
                    {log.timestamp.toLocaleString()}
                  </div>
                  <div className="truncate text-sm text-gray-600">
                    {log.adminEmail}
                  </div>
                  <div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionStyle(log.action)}`}>
                      {getActionLabel(log.action)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <pre className="whitespace-pre-wrap font-sans">
                      {JSON.stringify(log.details, null, 2)}
                    </pre>
                  </div>
                </div>
              ))}

              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : hasMore && (
                <div className="text-center py-4">
                  <button
                    onClick={() => fetchLogs()}
                    className="text-primary hover:text-primary-dark font-medium"
                  >
                    Cargar más logs
                  </button>
                </div>
              )}

              {filteredLogs.length === 0 && !loading && (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No hay logs que mostrar</p>
                  {searchTerm && (
                    <p className="text-gray-500 mt-2">
                      Prueba con otros términos de búsqueda
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}